import React from 'react';
import { Question, OASISFormData, FormValue } from './types';
import { RadioQuestion } from './questions/RadioQuestion';
import { TextQuestion } from './questions/TextQuestion';
import { DateQuestion } from './questions/DateQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { NumberQuestion } from './questions/NumberQuestion';
import {
  GridSpan,
  resolveQuestionSpan,
  getSubgroupChildSpanClass,
} from './OASISFormUtils';
import { getSelectedSubitems } from './utils';
import { QidTag } from '../dev/QidTag';

interface OASISQuestionRendererProps {
  question: Question;
  value: FormValue;
  onChange: (value: FormValue) => void;
  formData: OASISFormData;
  onFormDataChange?: (updates: Partial<OASISFormData>) => void;
  containerSpan?: GridSpan;
  highlightedQid?: string | null;
  /**
   * Qids currently staged from a previous visit (EMR-675) — displayed but not
   * saved. Marked so the clinician can tell them apart from their own answers.
   */
  stagedQids?: ReadonlySet<string>;
}

export const OASISQuestionRenderer: React.FC<OASISQuestionRendererProps> = ({
  question,
  value,
  onChange,
  formData,
  onFormDataChange,
  containerSpan = 12,
  highlightedQid,
  stagedQids,
}) => {
  const isHighlighted = highlightedQid === question.qid;
  const isStaged = stagedQids?.has(question.qid) === true;
  const uiDisabled = question.ui_editing_disabled === true;
  const activeSubitems = getSelectedSubitems(question, value);

  const renderSubitems = () => {
    if (activeSubitems.length === 0) return null;

    return (
      <div className='ml-3 mt-3 border-l-2 border-primary/20 pl-3'>
        <div className='grid grid-cols-12 gap-3'>
          {activeSubitems.map((subitem: Question) => {
            const subitemSpan = resolveQuestionSpan(subitem);
            const effectiveSubitemContainerSpan = Math.min(
              subitemSpan,
              containerSpan
            ) as GridSpan;
            return (
              <div
                key={subitem.qid}
                className={getSubgroupChildSpanClass(
                  subitemSpan,
                  containerSpan
                )}
              >
                <OASISQuestionRenderer
                  question={subitem}
                  value={formData[subitem.qid]}
                  onChange={(subValue: FormValue) => {
                    if (onFormDataChange) {
                      onFormDataChange({ [subitem.qid]: subValue });
                    } else {
                      onChange(subValue);
                    }
                  }}
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  containerSpan={effectiveSubitemContainerSpan}
                  stagedQids={stagedQids}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderQuestion = () => {
    switch (question.type) {
      case 'radio':
        return (
          <RadioQuestion
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
            disabled={uiDisabled}
          />
        );

      case 'text':
        return (
          <TextQuestion
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
            disabled={uiDisabled}
          />
        );

      case 'number':
        return (
          <NumberQuestion
            question={question}
            value={
              typeof value === 'number'
                ? value
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={onChange}
            disabled={uiDisabled}
          />
        );

      case 'date':
        return (
          <DateQuestion
            question={question}
            value={
              value instanceof Date
                ? value
                : typeof value === 'string'
                  ? value
                  : ''
            }
            onChange={onChange}
            disabled={uiDisabled}
          />
        );

      case 'multiselect':
        return (
          <MultiSelectQuestion
            question={question}
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
            disabled={uiDisabled}
          />
        );

      default:
        return (
          <div className='text-destructive text-xs'>
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  // Show a hint when the question has conditional subitems but hasn't been answered yet.
  const hasRevealableSubitems =
    question.subitems &&
    Object.values(question.subitems).some(arr => arr.length > 0);

  return (
    <div
      id={`oasis-q-${question.qid}`}
      className={`rounded-lg border px-3.5 py-3 overflow-hidden h-full transition-all duration-500 ${
        isHighlighted
          ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/30'
          : isStaged
            ? 'border-amber-400/70 bg-amber-50 dark:bg-amber-950/30'
            : 'border-border/40 bg-muted/30'
      }`}
    >
      {renderQuestion()}
      <span className='text-[9px] font-mono text-muted-foreground/50 select-all'>
        {question.oasis_ids?.length ? `${question.oasis_ids.join(', ')}` : ''}
      </span>
      <QidTag qid={question.qid} />
      {isStaged && (
        <span className='ml-1 whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-900 dark:bg-amber-900/50 dark:text-amber-100'>
          Copied · not saved
        </span>
      )}
      {hasRevealableSubitems && activeSubitems.length === 0 && (
        <p className='text-[11px] text-muted-foreground/70 mt-1.5 italic'>
          Answering may reveal more items
        </p>
      )}
      {renderSubitems()}
    </div>
  );
};
